// Bewegungen der Figuren aus dem Kampfverlauf (Phase Kern, T31, T30, K39). Der Kampf steht fest, bevor er abgespielt
// wird; daraus liest die Darstellung ab, wann jede Figur ausholt, wirft, getroffen wird, springt, geht oder fällt.
// Der Schlag oder Wurf sitzt im Bild genau dann, wenn der Treffer zählt (Projekt-Lead, 17.10.2026): Ausholen und Flug
// beginnen deshalb vor dem Treffer, so dass das Trefferbild und die Landung des Geschosses auf den Treffer fallen.
// Reine Darstellung: liest das Ergebnis der Simulation, schreibt nie hinein.
import { combatRules, getAbility, getSpecies } from '../../content/index.ts';
import { FrameFlag, FrameFlag2, type BattleResult, type Unit } from '../../sim/index.ts';
import { animSeconds, fxSeconds, secondsUntilFrame, secondsUntilStep, sprite, type ArtAnim, type ArtSheet } from '../art.ts';
import { byRadius } from '../practice/look.ts';

type P = { x: number; y: number };

/** Ein Angriff, eine Heilung oder ein Wurf; t ist der Moment, in dem er zählt. anim: eigene Bewegung, etwa rammen */
export type Strike = { t: number; ranged: boolean; release: number; anim?: string; /** K44: wen er trifft, für Blickrichtung und Ausfallschritt */ to: number };
/**
 * Ein Geschoss im Flug: los bei release, Treffer bei t. Version 3: fromPoint und toPoint (sonst wurf und Körpermitte),
 * impact: was beim Ankommen aufblitzt (null: nichts, sonst Funke oder Klatscher)
 */
/** K47: aim, wohin ein echtes Geschoss der Simulation fliegt (Feldeinheiten), hit: wen es dort trifft (sonst Boden oder Hindernis) */
export type Shot = { from: number; to: number; release: number; t: number; projectile: string; arc: number; heal: boolean; mud: boolean; fromPoint?: string; toPoint?: string; impact?: null; aim?: P; hit?: boolean };
/** K39: Sprung im Bogen, Huschen, Stoß, unter der Erde (Erdüberraschung); das Bild läuft von t0 bis t1 */
export type Leap = { t0: number; t1: number; from: P; to: P; style: 'arc' | 'dash' | 'slide' | 'burrow'; height: number; /** S5: Absprung aus dem Baum oder Landung darin */ fromTree: boolean; toTree: boolean };
/**
 * Eine eigene Bewegung für einen Kniff (Version 3), etwa zetern, graben, rollen. strong geht Angriffen vor: Der Kniff ist
 * der seltenere Moment, den man sehen soll; die Bewegungen sind kurz, höchstens ein Schlag fällt in sie hinein
 */
export type Act = { t0: number; t1: number; anim: string; strong: boolean };
/**
 * Ein Bild für einen Kniff an einer Figur (Version 3, T31): unit trägt es, owner macht den Kniff (seine Blickrichtung
 * zählt). Es zeigt sich von t0 bis t1; at: fester Platz am Boden statt an der Figur; point: statt des Punkts im Manifest
 */
export type Fx = { kind: 'effect' | 'status'; id: string; unit: number; owner: number; t0: number; t1: number; at?: P; point?: string };

type Track = {
  strikes: Strike[];
  hits: number[];
  down: number | null;
  leaps: Leap[];
  acts: Act[];
  /** Erdüberraschung: das Ziel wird kurz hochgehoben */
  bumps: number[];
  walk: (t: number) => boolean;
};
export type Motion = { tracks: Map<number, Track>; shots: Shot[]; dust: { t: number; x: number; y: number }[]; fx: Fx[] };

/** Wer womit wirft (manifest.json, usedBy): Spezies und Klasse beim normalen Angriff, sonst je Spezies */
const THROWS: Record<string, string> = {
  'eichhoernchen:beschuetzer': 'nuss',
  'waschbaer:adlerauge': 'kiesel',
  'waschbaer:beschuetzer': 'kiesel',
  // Fabi wählt noch zwischen Federpfeil und Wassertropfen (Parkplatz); bis dahin der Vorschlag des Grafik-Leads
  'eisvogel:adlerauge': 'federpfeil',
  'wasserspitzmaus:giftzahn': 'giftspucke',
  'feuersalamander:giftzahn': 'gifttropfen',
  hagelchen: 'hagelkorn',
  dreckschleuder: 'dreckklumpen',
  // G26 (Phase Kern): das Nieselchen wirft Blätter aus seinem Versteck; aus der Nähe schlägt es mit dem Blattschwert
  nieselchen: 'blatt',
};
/** Wer mal wirft, mal zuschlägt: wirft nur, wenn das Ziel weiter weg ist */
const MIXED = new Set(['nieselchen']);
/** Kniffe mit eigenem Geschoss */
const KNIFF_THROWS: Record<string, string> = {
  kieselwurf: 'kiesel',
  nussgeschenk: 'gluecksnuss',
  schlammspritzer: 'schlammspritzer',
  'auf-die-augen': 'dreckklumpen',
  hagelschauer: 'hagelkorn',
  'wunden-auswaschen': 'waschwasser',
};
const MUD = new Set(['dreckklumpen', 'schlammspritzer']);
/** Version 3: Kniffe, die beim Zuschlagen eine eigene Bewegung haben */
const KNIFF_ANIMS: Record<string, string> = { rammstoss: 'rammen', blattschwert: 'doppelhieb', finte: 'finte' };
/** Version 3: Kniffe mit eigenem Trefferbild am Getroffenen statt des Funkens */
export const KNIFF_HITS: Record<string, string> = {
  messerhieb: 'hieb',
  blattschwert: 'blatthieb',
  rammstoss: 'rammwucht',
  stachelball: 'stachelfunken',
  glitzerklau: 'glitzer',
  erdueberraschung: 'erdausbruch',
  // S13 (Runde 7): Späne am Getroffenen, wenn der Baum kracht; der Biss beim Auftauchen des Otters
  'baum-faellen': 'holzsplitter',
  abtauchen: 'bissspur',
};
/** Kniffe mit eigener Bewegung statt Ausholen (rollen, auftauchen, der Baum kracht von selbst, der Stein hüpft) */
const OWN_MOVE = new Set(['stachelball', 'erdueberraschung', 'abtauchen', 'baum-faellen', 'flitschstein']);
/** K39: Wer im Bogen springt und wer flach huscht; alle anderen springen im Bogen */
const DASHERS = new Set(['feuersalamander']);

const SPEED: Record<string, number> = { Bogen: 55, 'flacher Bogen': 70, gerade: 95, 'gerade, schnell': 140 };
const ARC: Record<string, number> = { Bogen: 0.28, 'flacher Bogen': 0.12, gerade: 0, 'gerade, schnell': 0 };

const LEAP_SECONDS = { arc: 0.33, dash: 0.25, slide: 0.18, burrow: 0.3 } as const;

let cache: { result: BattleResult | null; motion: Motion | null } = { result: null, motion: null };

export function motionOf(r: BattleResult): Motion {
  if (cache.result === r && cache.motion) return cache.motion;
  const motion = build(r);
  cache = { result: r, motion };
  return motion;
}

function build(r: BattleResult): Motion {
  const tick = combatRules.tickSeconds;
  const frameAt = (t: number) => r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.round(t / tick)))];
  const posAt = (uid: number, t: number): P => {
    const d = frameAt(t)?.units[uid];
    return d ? { x: d[0], y: d[1] } : { x: 0, y: 0 };
  };
  const flagAt = (uid: number, t: number, flag: number) => ((frameAt(t)?.units[uid]?.[4] ?? 0) & flag) !== 0;
  const onTreeAt = (uid: number, t: number) => flagAt(uid, t, FrameFlag.onTree);
  const tracks = new Map<number, Track>();
  for (const u of r.units) {
    const xs = r.frames.map((f) => f.units[u.uid]!);
    tracks.set(u.uid, {
      strikes: [],
      hits: [],
      down: null,
      leaps: [],
      acts: [],
      bumps: [],
      // T31: Läuft er gerade? Mehr als ein paar Schritte in einer Zehntelsekunde, ohne Sprung
      walk: (t: number) => {
        const i = Math.min(xs.length - 1, Math.max(0, Math.floor(t / tick)));
        const j = Math.max(0, i - 2);
        const a = xs[j];
        const b = xs[i];
        if (!a || !b) return false;
        const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
        return d > 0.35 && d < 6;
      },
    });
  }
  const shots: Shot[] = [];
  const dust: { t: number; x: number; y: number }[] = [];
  const lastHit = new Map<number, number>();

  const addStrike = (u: Unit, target: number, t: number, projectile: string | null, heal = false, anim?: string, extra: Partial<Shot> = {}) => {
    const tr = tracks.get(u.uid);
    if (!tr) return;
    const prev = tr.strikes[tr.strikes.length - 1];
    if (prev && Math.abs(prev.t - t) < 1e-6) {
      // Kieselwurf und Co.: ein Wurf, mehrere Geschosse; trifft der Kniff im selben Moment wie ein Angriff, zeigt das Bild den Kniff
      if (projectile) shots.push({ ...shot(u, target, prev.release, t, projectile, heal), ...extra });
      if (anim) prev.anim = anim;
      return;
    }
    // G26 Blattschwert: zwei schnelle Hiebe in einer Bewegung (doppelhieb, hitSteps)
    if (prev?.anim === 'doppelhieb' && anim === 'doppelhieb' && t - prev.t > 1e-6 && t - prev.t < 0.3) return;
    if (!projectile) {
      tr.strikes.push({ t, ranged: false, release: t, to: target, ...(anim ? { anim } : {}) });
      return;
    }
    const flight = sprite('projectile', projectile)?.flight ?? 'Bogen';
    const d = Math.hypot(posAt(target, t).x - posAt(u.uid, t).x, posAt(target, t).y - posAt(u.uid, t).y);
    const seconds = Math.min(0.55, Math.max(0.1, d / (SPEED[flight] ?? 70)));
    tr.strikes.push({ t, ranged: true, release: t - seconds, to: target, ...(anim ? { anim } : {}) });
    shots.push({ ...shot(u, target, t - seconds, t, projectile, heal), ...extra });
  };
  const shot = (u: Unit, to: number, release: number, t: number, projectile: string, heal: boolean): Shot => {
    const flight = sprite('projectile', projectile)?.flight ?? 'Bogen';
    return { from: u.uid, to, release, t, projectile, arc: ARC[flight] ?? 0.2, heal, mud: MUD.has(projectile) };
  };
  const fx: Fx[] = [];
  /** Ein Bild für einen Kniff; ohne Ende einmal durch */
  const addFx = (kind: Fx['kind'], id: string, unit: number, owner: number, t0: number, t1?: number, extra: Partial<Fx> = {}) => {
    const sp = sprite(kind, id);
    if (!sp) return;
    fx.push({ kind, id, unit, owner, t0, t1: t1 ?? t0 + fxSeconds(sp), ...extra });
  };
  const lastHitFx = new Map<string, number>();
  const throwOf = (u: Unit, target?: number, t?: number): string | null => {
    if (MIXED.has(u.defId) && target !== undefined && t !== undefined) {
      const a = posAt(u.uid, t);
      const o = posAt(target, t);
      const far = Math.hypot(o.x - a.x, o.y - a.y) - u.radius - (r.units[target]?.radius ?? 0) > 6;
      return far ? (THROWS[u.defId] ?? null) : null;
    }
    if (u.range < 15) return null;
    return THROWS[`${u.defId}:${u.friendClass ?? ''}`] ?? THROWS[u.defId] ?? null;
  };

  // K47: Im Übungsufer fliegen die Geschosse in der Simulation; Wurf und Flug kommen aus deren Ereignissen
  const shotEnds = new Map<number, { t: number; x: number; y: number; unit: number | null; obstacle: string | null }>();
  const thrown = new Set<string>();
  for (const e of r.events) {
    if (e.type === 'shotEnd') shotEnds.set(e.id, e);
    else if (e.type === 'shot') thrown.add(`${e.from}:${e.t}`);
  }
  /** S13 Finte: wer in diesem Moment getäuscht wurde (t:uid) */
  const feinted = new Set(r.events.filter((e) => e.type === 'kniff' && e.kniff === 'finte' && e.target !== null).map((e) => `${e.t}:${(e as { target: number }).target}`));
  for (const e of r.events) {
    if (e.type === 'shot') {
      const tr = tracks.get(e.from);
      const end = shotEnds.get(e.id);
      const flight = sprite('projectile', e.art)?.flight ?? 'Bogen';
      if (tr) tr.strikes.push({ t: e.t, ranged: true, release: e.t, to: e.to });
      shots.push({
        from: e.from,
        to: end?.unit ?? e.to,
        release: e.t,
        t: end?.t ?? e.arrive,
        projectile: e.art,
        arc: e.style === 'straight' ? 0 : (ARC[flight] ?? 0.2),
        heal: e.style === 'homing',
        mud: MUD.has(e.art),
        ...(e.style !== 'homing' ? { aim: end ? { x: end.x, y: end.y } : { x: e.tx, y: e.ty }, hit: end?.unit !== null && end?.unit !== undefined } : {}),
        ...(end && end.unit === null && !end.obstacle ? { impact: null } : {}),
      });
      continue;
    }
    if (e.type === 'strike' && thrown.has(`${e.unit}:${e.t}`)) continue;
    if (e.type === 'strike') {
      const u = r.units[e.unit];
      if (!u) continue;
      const projectile = e.kind === 'heal' ? 'heilmoos' : e.kind === 'shield' ? 'dreckklumpen' : throwOf(u, e.target, e.t);
      addStrike(u, e.target, e.t, projectile, e.kind === 'heal');
    } else if (e.type === 'damage' && e.source === 'kniff' && e.from !== null) {
      // Kniffe, die treffen: ausholen wie beim Angriff, Fernkämpfer werfen; jede Figur hat einen Kniff
      const u = r.units[e.from];
      if (!u || u.kind === 'tree') continue;
      // S13 Finte: Das getäuschte Sturmwesen haut seinen Nachbarn, ein ganz gewöhnlicher Schlag ins Leere
      if (feinted.has(`${e.t}:${u.uid}`)) {
        addStrike(u, e.to, e.t, null);
        continue;
      }
      const k = u.kniff;
      const projectile = (k && KNIFF_THROWS[k]) ?? (k && KNIFF_ANIMS[k] ? null : throwOf(u, e.to, e.t));
      // Stachelball, Erdüberraschung, Abtauchen, Baum fällen und Flitschstein haben ihre eigene Bewegung, dort kein Ausholen
      if (!k || !OWN_MOVE.has(k)) addStrike(u, e.to, e.t, projectile, false, k ? KNIFF_ANIMS[k] : undefined);
      // Version 3: eigenes Trefferbild am Getroffenen (Hieb, Rammwucht, Stachelfunken …); bei schnellen Folgen nicht jedes Mal
      const hitFx = k ? KNIFF_HITS[k] : undefined;
      if (hitFx) {
        const key = `${u.uid}:${e.to}`;
        if (e.t - (lastHitFx.get(key) ?? -Infinity) > 0.3 || k === 'blattschwert') {
          lastHitFx.set(key, e.t);
          addFx('effect', hitFx, e.to, u.uid, e.t);
          if (k === 'erdueberraschung') tracks.get(e.to)?.bumps.push(e.t);
        }
      }
    }
    if (e.type === 'kniff' && e.target !== null && KNIFF_THROWS[e.kniff] && !thrown.has(`${e.unit}:${e.t}`)) {
      // Kniffe ohne Schaden mit Geschoss: Schlammspritzer, Auf die Augen, Nussgeschenk
      const u = r.units[e.unit];
      if (!u) continue;
      const hitsLater = r.events.some((o) => o.type === 'damage' && o.source === 'kniff' && o.from === e.unit && Math.abs(o.t - e.t) < 1e-6);
      if (!hitsLater) addStrike(u, e.target, e.t, KNIFF_THROWS[e.kniff]!, e.kniff === 'nussgeschenk' || e.kniff === 'wunden-auswaschen');
    }
    if (e.type === 'damage' && (e.amount > 0 || (e.blocked ?? 0) > 0) && e.source !== 'poison' && e.source !== 'dot') {
      const tr = tracks.get(e.to);
      const last = lastHit.get(e.to) ?? -1;
      if (tr && e.t - last > 0.06) {
        tr.hits.push(e.t);
        lastHit.set(e.to, e.t);
      }
    } else if (e.type === 'down') {
      const tr = tracks.get(e.unit);
      if (tr) tr.down = e.t;
    } else if (e.type === 'move') {
      const tr = tracks.get(e.unit);
      const u = r.units[e.unit];
      if (!tr || !u) continue;
      const prev = tr.leaps[tr.leaps.length - 1];
      const sameTick = !!prev && Math.abs(prev.t1 - e.t) < 1e-6 && prev.t0 < e.t;
      const from = sameTick ? prev.to : posAt(e.unit, e.t - tick);
      const to = { x: e.x, y: e.y };
      const d = Math.hypot(to.x - from.x, to.y - from.y);
      if (d < 1.5) continue;
      // Stöße und Erschrecken passieren nach dem Treffer, Sprünge davor, damit die Landung auf den Treffer fällt
      const after = e.reason === 'knockback' || e.reason === 'scared' || (e.reason === 'return' && sameTick);
      // R27 Erdüberraschung: unter der Erde zum Ziel, ohne Bogen
      const burrow = e.reason === 'leap' && flagAt(e.unit, e.t - tick, FrameFlag.burrowed);
      // K48: Beim Rammstoß stürmt der Treibholzer flach vor
      const style = e.reason === 'knockback' || e.reason === 'scared' ? 'slide' : e.reason === 'charge' ? 'dash' : burrow ? 'burrow' : DASHERS.has(u.defId) ? 'dash' : 'arc';
      const D = LEAP_SECONDS[style];
      const start = after ? e.t : Math.max(e.t - D, prev ? prev.t1 : 0, 0);
      const end = after ? e.t + D : e.t;
      if (end - start < 0.05) continue;
      const fromTree = !sameTick && onTreeAt(e.unit, e.t - tick);
      const toTree = onTreeAt(e.unit, e.t + tick);
      tr.leaps.push({ t0: start, t1: end, from, to, style, height: style === 'arc' ? Math.min(12, Math.max(3, d * 0.3)) : 0, fromTree, toTree });
      // Staub bei der Landung (K39)
      if (style !== 'burrow') dust.push({ t: end, x: to.x, y: to.y });
    }
  }
  kniffArt(r, tracks, shots, addStrike, addFx, posAt, flagAt);
  // S13 Biber, Warnklatscher (Runde 7): Zu Beginn des Kampfs reckt sich jeder Biber und klatscht zweimal mit der Kelle
  for (const u of r.units) {
    if (u.side !== 'friend' || u.kind !== 'friend') continue;
    let passive = '';
    try {
      passive = getSpecies(u.defId).passive;
    } catch {
      continue;
    }
    if (passive !== 'warnklatscher') continue;
    const a = sprite('species', u.defId)?.sheet?.animations.klatschen;
    const tr = tracks.get(u.uid);
    if (!a || !tr) continue;
    const t0 = 0.15;
    tr.acts.push({ t0, t1: t0 + animSeconds(a), anim: 'klatschen', strong: true });
    for (const step of a.hitSteps ?? [1]) addFx('effect', 'klatsch', u.uid, u.uid, t0 + secondsUntilStep(a, step));
  }
  realtimeArt(r, tracks, addFx, posAt);
  // K38, Version 3: Eigenschaften, die einen Moment haben
  for (const u of r.units) {
    // Unruhig: Doppelpfeil, wenn es das Ziel wechselt (Ziel im Bild, Feld 6)
    if (u.traits.includes('unruhig')) {
      let last = -1;
      for (const f of r.frames) {
        const tgt = f.units[u.uid]?.[6] ?? -1;
        if (tgt >= 0 && last >= 0 && tgt !== last) addFx('status', 'anzeige-unruhig', u.uid, u.uid, f.t);
        if (tgt >= 0) last = tgt;
      }
    }
    // Zäh: graue Splitter statt des Funkens, wenn man es trifft
    if (u.traits.includes('zaeh')) for (const h of tracks.get(u.uid)?.hits ?? []) addFx('status', 'anzeige-zaeh-treffer', u.uid, u.uid, h);
  }
  // Verpeilt: das Fragezeichen, wenn es seinen Kniff vergisst
  for (const e of r.events) if (e.type === 'status' && e.status === 'confused' && e.via === 'trait:verpeilt') addFx('status', 'anzeige-verpeilt', e.unit, e.unit, e.t);
  // T9: Flug-Kniffe wie der Sturzflug fliegen hin und zurück (wie bisher, nur als Sprung mit Strichen)
  for (const e of r.events) {
    if (e.type !== 'kniff' || e.target === null || getAbility(e.kniff).visual !== 'flight') continue;
    const tr = tracks.get(e.unit);
    if (!tr) continue;
    const home = posAt(e.unit, e.t);
    const prey = posAt(e.target, e.t);
    const u = r.units[e.unit]!;
    const v = r.units[e.target]!;
    const d = Math.hypot(prey.x - home.x, prey.y - home.y) || 1;
    const reach = Math.max(0, d - u.radius - v.radius);
    const at = { x: home.x + ((prey.x - home.x) / d) * reach, y: home.y + ((prey.y - home.y) / d) * reach };
    tr.leaps.push({ t0: e.t - 0.15, t1: e.t, from: home, to: at, style: 'arc', height: 2, fromTree: false, toTree: false });
    tr.leaps.push({ t0: e.t, t1: e.t + 0.2, from: at, to: home, style: 'arc', height: 2, fromTree: false, toTree: false });
  }
  for (const tr of tracks.values()) tr.leaps.sort((a, b) => a.t0 - b.t0);
  return { tracks, shots, dust, fx };
}

type AddStrike = (u: Unit, target: number, t: number, projectile: string | null, heal?: boolean, anim?: string, extra?: Partial<Shot>) => void;
type AddFx = (kind: Fx['kind'], id: string, unit: number, owner: number, t0: number, t1?: number, extra?: Partial<Fx>) => void;

/**
 * Version 3 des Manifests (Grafik-Lead, 17.10.2026): Kniffe mit eigenen Bildern und Bewegungen, nach den Abläufen in
 * den Hinweisen des Manifests. Jeder Kniff hängt an seinem Ereignis in der Simulation; was eine Weile wirkt, endet mit
 * dem Zustand, den der Kniff setzt.
 */
function kniffArt(
  r: BattleResult,
  tracks: Map<number, Track>,
  shots: Shot[],
  addStrike: AddStrike,
  addFx: AddFx,
  posAt: (uid: number, t: number) => P,
  flagAt: (uid: number, t: number, flag: number) => boolean,
): void {
  const ev = r.events;
  const same = (a: number, b: number) => Math.abs(a - b) < 1e-6;
  /** Bis wann ein Zustand wirkt, den der Kniff im selben Moment gesetzt hat */
  const until = (uid: number, status: string, t: number, kniff: string): number | null => {
    for (const e of ev) {
      if (e.t > t + 1e-6) break;
      if (e.type === 'status' && e.unit === uid && e.status === status && same(e.t, t) && (e.via ?? '').includes(`kniff:${kniff}`)) return e.until;
    }
    return null;
  };
  /** Bis das Bild den Zustand nicht mehr zeigt, höchstens `max` Sekunden */
  const flagEnd = (uid: number, from: number, flag: number, max: number): number => {
    const tick = combatRules.tickSeconds;
    for (let t = from + tick; t < from + max; t += tick) if (!flagAt(uid, t, flag)) return t;
    return from + max;
  };
  for (let i = 0; i < ev.length; i++) {
    const e = ev[i]!;
    if (e.type !== 'kniff') continue;
    const u = r.units[e.unit];
    const tr = tracks.get(e.unit);
    if (!u || !tr) continue;
    const t = e.t;
    switch (e.kniff) {
      case 'giftspritzer': {
        // S8: spritzen, je ein Giftklecks im Bogen zu jedem Ziel, dort klebt die Giftflatsche
        const targets: number[] = [];
        for (let j = i - 1; j >= 0 && same(ev[j]!.t, t); j--) {
          const o = ev[j]!;
          if (o.type === 'poison' && r.units[o.unit]?.side !== u.side && !targets.includes(o.unit)) targets.unshift(o.unit);
        }
        for (const to of targets) {
          addStrike(u, to, t, 'giftklecks', false, 'spritzen', { impact: null });
          addFx('effect', 'giftflatsche', to, u.uid, t);
        }
        const s = tr.strikes[tr.strikes.length - 1];
        if (targets.length && s) addFx('effect', 'giftspritzer', u.uid, u.uid, s.release, undefined, { point: 'kopf' });
        break;
      }
      case 'gezeter': {
        // S8: zetern mit Zackenblitzen am Kopf; wer zurückweicht, erschrickt
        tr.acts.push({ t0: t - 0.15, t1: t + 0.7, anim: 'zetern', strong: true });
        addFx('effect', 'gezeter', u.uid, u.uid, t - 0.15, t + 0.7);
        for (let j = i - 1; j >= 0 && same(ev[j]!.t, t); j--) {
          const o = ev[j]!;
          if (o.type === 'move' && o.reason === 'scared') addFx('effect', 'erschrocken', o.unit, u.uid, t);
        }
        break;
      }
      case 'bau-graben': {
        // R27: der Dachs gräbt, der Erdwall steht, solange der Bau wirkt; die Nachbarn bekommen einen kleinen Wall
        tr.acts.push({ t0: t, t1: t + 0.56, anim: 'graben', strong: true });
        const end = until(u.uid, 'guarded', t, e.kniff) ?? t + 4;
        for (const id of ['erdmulde', 'erdwall-hinten', 'erdwall-vorn']) addFx('effect', id, u.uid, u.uid, t, end);
        for (let j = i - 1; j >= 0 && same(ev[j]!.t, t); j--) {
          const o = ev[j]!;
          if (o.type === 'status' && o.status === 'guarded' && o.unit !== u.uid && (o.via ?? '').includes(`kniff:${e.kniff}`)) addFx('effect', 'erdschutz', o.unit, u.uid, t, o.until);
        }
        break;
      }
      case 'erdueberraschung': {
        // R27: eingraben, das Erdloch bleibt; unter der Erde zum Ziel, dort auftauchen, der Boden bricht auf
        const emerge = ev.find((o) => o.type === 'move' && o.unit === u.uid && o.reason === 'leap' && o.t > t + 1e-6 && o.t < t + 3);
        const te = emerge?.t ?? t + 0.6;
        tr.acts.push({ t0: t - 0.2, t1: te, anim: 'eingraben', strong: true });
        tr.acts.push({ t0: te, t1: te + 0.34, anim: 'auftauchen', strong: true });
        addFx('effect', 'erdloch', u.uid, u.uid, t, te + 0.5, { at: posAt(u.uid, t) });
        break;
      }
      case 'abtauchen': {
        // S13 Otter (Runde 7): kopfüber in den Boden wie ins Wasser; der kleine Wasserring bleibt, solange er unten ist,
        // Wellen ziehen mit ihm zum Ziel, dort schießt er heraus und beißt (bissspur über KNIFF_HITS)
        const emerge = ev.find((o) => o.type === 'move' && o.unit === u.uid && o.reason === 'leap' && o.t > t + 1e-6 && o.t < t + 3);
        const te = emerge?.t ?? t + 0.7;
        tr.acts.push({ t0: t, t1: te, anim: 'abtauchen', strong: true });
        tr.acts.push({ t0: te, t1: te + 0.34, anim: 'auftauchen', strong: true });
        addFx('effect', 'wellen', u.uid, u.uid, t + 0.3, te);
        addFx('effect', 'wellen', u.uid, u.uid, te, te + 0.6, { at: posAt(u.uid, t) });
        break;
      }
      case 'stoeckchenwall': {
        // S13 Biber (Runde 7): Er klopft Pflöcke fest; um jeden, der den Kranz bekommt, schießen Stöckchen aus dem Boden
        // und bleiben, solange der Kranz Schaden abfängt (shielded)
        tr.acts.push({ t0: t, t1: t + 0.74, anim: 'bauen', strong: true });
        for (let j = i - 1; j >= 0 && same(ev[j]!.t, t); j--) {
          const o = ev[j]!;
          if (o.type !== 'status' || o.status !== 'shielded' || o.via !== `kniff:${e.kniff}`) continue;
          const ring = byRadius('status', 'stoeckchenwall-', r.units[o.unit]?.radius ?? 3.2);
          if (ring) addFx('status', ring.id, o.unit, u.uid, t, flagEnd(o.unit, t, FrameFlag.shielded, 30));
        }
        break;
      }
      case 'finte': {
        // S13 Fuchs (Runde 7): Der Schlag des Getäuschten fährt ins Leere (leadMs vor dem Treffer, mit Bild 1 der Finte)
        const lead = (sprite('effect', 'ins-leere')?.leadMs ?? 150) / 1000;
        if (e.target !== null) addFx('effect', 'ins-leere', e.target, u.uid, t - lead);
        break;
      }
      case 'ablenkung': {
        // S13 Fuchs (Runde 7): Solange die Ablenkung wirkt, duckt er sich weg, statt getroffen zu zucken; der Schlag fährt
        // daneben durch die Luft
        let end = t + 3;
        for (let j = i - 1; j >= 0 && same(ev[j]!.t, t); j--) {
          const o = ev[j]!;
          if (o.type === 'status' && o.unit === u.uid && o.status === 'guarded' && o.via === `kniff:${e.kniff}`) end = o.until;
        }
        let last = -Infinity;
        for (const o of ev) {
          if (o.t <= t + 1e-6 || o.t > end) continue;
          if (o.type !== 'damage' || o.to !== u.uid || o.source !== 'hit' || o.from === null || o.t - last < 0.3) continue;
          last = o.t;
          tr.acts.push({ t0: o.t, t1: o.t + 0.33, anim: 'ausweichen', strong: true });
          addFx('effect', 'daneben', u.uid, o.from, o.t);
          tr.hits = tr.hits.filter((h) => !same(h, o.t));
        }
        break;
      }
      case 'flitschstein': {
        // S13 Otter (Runde 7): Wurf, dann hüpft der Stein von Sturmwesen zu Sturmwesen, je Sprung 0,15 s; wo er aufsetzt, platscht es
        const hit: number[] = [];
        for (let j = i - 1; j >= 0 && same(ev[j]!.t, t); j--) {
          const o = ev[j]!;
          if (o.type === 'damage' && o.from === u.uid && o.source === 'kniff' && !hit.includes(o.to)) hit.unshift(o.to);
        }
        if (!hit.length && e.target !== null) hit.push(e.target);
        hit.forEach((to, n) => {
          const at = t + n * 0.15;
          if (n === 0) addStrike(u, to, t, 'flitschstein', false, 'wurf', { toPoint: 'fuss', impact: null });
          else shots.push({ from: hit[n - 1]!, to, release: at - 0.15, t: at, projectile: 'flitschstein', arc: 0.12, heal: false, mud: false, fromPoint: 'fuss', toPoint: 'fuss', impact: null });
          addFx('effect', 'platsch', to, u.uid, at);
        });
        break;
      }
      case 'stachelball': {
        // F35: rollt hin und kreiselt; die Wirbelbögen, solange er kreiselt
        const tick = combatRules.tickSeconds;
        let start: number | null = null;
        for (let x = t; x < t + 5; x += tick) {
          if (flagAt(u.uid, x, FrameFlag.spinning)) {
            start = x;
            break;
          }
        }
        const end = start === null ? t + 4 : flagEnd(u.uid, start, FrameFlag.spinning, 6);
        tr.acts.push({ t0: t, t1: end, anim: 'rollen', strong: true });
        if (start !== null) addFx('effect', 'kreiselspur', u.uid, u.uid, start, end);
        break;
      }
      case 'tiefer-biss': {
        // F35: die Zähnchen schnappen genau beim Biss zu (leadMs)
        const lead = (sprite('effect', 'bissspur')?.leadMs ?? 0) / 1000;
        if (e.target !== null) addFx('effect', 'bissspur', e.target, u.uid, t - lead);
        break;
      }
      case 'raserei': {
        addFx('effect', 'heisshunger', u.uid, u.uid, t, until(u.uid, 'empowered', t, e.kniff) ?? t + 4);
        break;
      }
      case 'hautgift': {
        addFx('effect', 'hautgift', u.uid, u.uid, t, until(u.uid, 'toxic', t, e.kniff) ?? t + 5);
        break;
      }
      case 'wunden-auswaschen': {
        // F35: der Wasserschwall fliegt (KNIFF_THROWS), beim Freund wäscht er aus
        if (e.target !== null) addFx('effect', 'auswaschen', e.target, u.uid, t);
        break;
      }
      case 'glitzerklau': {
        // S8: das glänzende Ding fliegt vom Ziel zurück zur Elster, an ihren Punkt ruf
        if (e.target !== null && sprite('projectile', 'beute')) {
          shots.push({ from: e.target, to: u.uid, release: t + 0.08, t: t + 0.5, projectile: 'beute', arc: 0.28, heal: false, mud: false, fromPoint: 'mitte', toPoint: 'ruf', impact: null });
        }
        break;
      }
      case 'rammstoss': {
        // G-Treibholzer: Staub hinter ihm beim Anlauf, bis zum Aufprall (rammen, hitFrame 1)
        const a = sprite('enemy', u.defId)?.sheet?.animations.rammen;
        const lead = a ? secondsUntilFrame(a, a.hitFrame ?? 1) : 0.15;
        addFx('effect', 'rammstaub', u.uid, u.uid, t - lead, t);
        break;
      }
      case 'grollen': {
        // G24: grollen, Wellen nach beiden Seiten (zaghaft zeigt die Anzeige, solange es wirkt)
        tr.acts.push({ t0: t - 0.25, t1: t + 0.6, anim: 'grollen', strong: true });
        addFx('effect', 'grollwelle', u.uid, u.uid, t - 0.25, t + 0.6);
        break;
      }
      case 'hagelschauer':
      case 'blitzschnell': {
        addFx('effect', e.kniff, u.uid, u.uid, t, until(u.uid, 'hasted', t, e.kniff) ?? t + 4);
        break;
      }
      case 'doppelsprung': {
        addFx('effect', 'boing', u.uid, u.uid, t, undefined, { at: posAt(u.uid, t) });
        break;
      }
    }
  }
}

/** K46: zweite Bitmaske eines Bilds (FrameFlag2), 0 außerhalb des Übungsufers */
export function flags2At(r: BattleResult, uid: number, T: number): number {
  const f = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.round(T / combatRules.tickSeconds)))];
  return f?.units[uid]?.[8] ?? 0;
}

/**
 * K46 bis K52, Übungsufer: die neuen Kniffe nach den Bildern, die es schon gibt. Bau graben: eingraben, wühlen (unterwegs
 * nur der Erdhügel), auftauchen, graben, dann Mulde und Erdwall, solange der Bau schützt. Stachelball: rollen und
 * kreiseln. Herbeiwinken: Der Igel rollt, der Dachs wühlt, der Eisvogel fliegt. Sturzflug: hoch hinauf und hinab aufs
 * Ziel, dann zurück. Rammstoß mit Ansage: Staub beim Anlauf, solange er ausholt
 */
function realtimeArt(r: BattleResult, tracks: Map<number, Track>, addFx: AddFx, posAt: (uid: number, t: number) => P): void {
  const tick = combatRules.tickSeconds;
  const anim = (uid: number, name: string) => {
    const u = r.units[uid];
    const sp = u ? (u.kind === 'stormling' ? sprite('enemy', u.defId) : sprite('species', u.defId)) : undefined;
    return sp?.sheet?.animations[name];
  };
  /** von t an, solange die Bitmaske flag2 gilt (höchstens max Sekunden): Beginn und Ende */
  const span = (uid: number, t: number, flag: number, max: number): [number, number] | null => {
    let a: number | null = null;
    for (let x = t; x < t + max; x += tick) {
      const on = (flags2At(r, uid, x) & flag) !== 0;
      if (on && a === null) a = x;
      if (!on && a !== null) return [a, x];
    }
    return a === null ? null : [a, t + max];
  };
  for (const e of r.events) {
    if (e.type === 'kniff') {
      const tr = tracks.get(e.unit);
      if (!tr) continue;
      const t = e.t;
      if (e.kniff === 'bau-graben-mulde') {
        const tunnel = span(e.unit, t, FrameFlag2.tunneling, 6);
        const te = tunnel ? tunnel[1] : t + 0.8;
        const dig = span(e.unit, te - tick, FrameFlag2.digging, 3);
        const td = dig ? dig[1] : te + 0.9;
        const inMs = anim(e.unit, 'eingraben') ? animSeconds(anim(e.unit, 'eingraben')!) : 0.8;
        tr.acts.push({ t0: t, t1: Math.min(te, t + inMs), anim: 'eingraben', strong: true });
        if (te > t + inMs) tr.acts.push({ t0: t + inMs, t1: te, anim: 'wuehlen', strong: true });
        tr.acts.push({ t0: te, t1: te + 0.34, anim: 'auftauchen', strong: true });
        tr.acts.push({ t0: te + 0.34, t1: td, anim: 'graben', strong: true });
        addFx('effect', 'erdloch', e.unit, e.unit, t, te + 0.6, { at: posAt(e.unit, t) });
        let end = td + 4;
        for (const o of r.events) {
          if (o.t < td - 1e-6) continue;
          if (o.t > td + 1e-6) break;
          if (o.type === 'status' && o.status === 'guarded' && (o.via ?? '').includes('kniff:bau-graben-mulde')) {
            if (o.unit === e.unit) end = o.until;
            else addFx('effect', 'erdschutz', o.unit, e.unit, td, o.until);
          }
        }
        // Übungsufer (Version 7): Der Boden rundum sackt ab; zuerst, damit Erdmulde und Erdwall darüber liegen
        addFx('effect', 'absacken', e.unit, e.unit, td, end, { at: posAt(e.unit, td) });
        for (const id of ['erdmulde', 'erdwall-hinten', 'erdwall-vorn']) addFx('effect', id, e.unit, e.unit, td, end);
      } else if (e.kniff === 'stachelball-ort') {
        const roll = span(e.unit, t, FrameFlag2.rolling, 4);
        const start = roll ? roll[1] : t;
        let end = start + 2.5;
        for (let x = start; x < start + 6; x += tick) {
          if ((flagsAt(r, e.unit, x) & FrameFlag.spinning) === 0 && x > start + tick) {
            end = x;
            break;
          }
        }
        tr.acts.push({ t0: t, t1: end, anim: 'rollen', strong: true });
        addFx('effect', 'kreiselspur', e.unit, e.unit, start, end);
      } else if (e.kniff === 'wunden-auswaschen-winken' && e.target !== null && e.target !== e.unit) {
        const f = r.units[e.target];
        const ft = tracks.get(e.target);
        if (!f || !ft) continue;
        const way = span(e.target, t, FrameFlag2.beckoned, 4);
        const ta = way ? way[1] : t + 0.8;
        // Der Waschbär winkt (Version 7: winken, dazu herbeiwinken an der Pfote winkhand), mindestens anderthalb Schwünge
        // lang, damit man es auch sieht, wenn der Freund schnell da ist (Grafik-Lead)
        const wave = Math.max(ta, t + 0.84);
        tr.acts.push({ t0: t, t1: wave, anim: 'winken', strong: true });
        addFx('effect', 'herbeiwinken', e.unit, e.unit, t, wave);
        if (f.defId === 'igel') ft.acts.push({ t0: t, t1: ta, anim: 'rollen', strong: true });
        else if (f.defId === 'dachs') {
          ft.acts.push({ t0: t, t1: ta, anim: 'wuehlen', strong: true });
          ft.acts.push({ t0: ta, t1: ta + 0.34, anim: 'auftauchen', strong: true });
        } else {
          // Wer fliegen kann, fliegt im Bogen herüber; alle anderen laufen schnell
          const from = posAt(e.target, t);
          const to = posAt(e.target, ta);
          if (anim(e.target, 'sprung')) ft.leaps.push({ t0: t, t1: ta, from, to, style: 'arc', height: Math.min(10, Math.max(3, Math.hypot(to.x - from.x, to.y - from.y) * 0.25)), fromTree: false, toTree: false });
        }
      } else if (e.kniff === 'schwachstelle-ausmachen') {
        // Er zielt kurz (das Ausholen des Wurfs, ohne Geschoss); die Zielscheibe zeigt das Zeichen am Sturmwesen
        tr.acts.push({ t0: t - 0.1, t1: t + 0.4, anim: 'wurf', strong: false });
      }
    } else if (e.type === 'heal' && e.via === 'kniff:wunden-auswaschen-winken' && e.from !== e.to) {
      addFx('effect', 'auswaschen', e.to, e.from, e.t);
    } else if (e.type === 'swoop') {
      // K52 Sturzflug: hoch hinauf und steil hinab aufs Ziel, dann zurück an seinen Platz
      const tr = tracks.get(e.unit);
      const u = r.units[e.unit];
      const v = r.units[e.target];
      if (!tr || !u || !v) continue;
      const home = posAt(e.unit, e.t);
      const prey = posAt(e.target, e.at);
      const d = Math.hypot(prey.x - home.x, prey.y - home.y) || 1;
      const reach = Math.max(0, d - u.radius - v.radius);
      const at = { x: home.x + ((prey.x - home.x) / d) * reach, y: home.y + ((prey.y - home.y) / d) * reach };
      tr.leaps.push({ t0: e.t, t1: e.at, from: home, to: at, style: 'arc', height: Math.min(16, 6 + d * 0.15), fromTree: false, toTree: false });
      tr.leaps.push({ t0: e.at, t1: e.at + 0.45, from: at, to: home, style: 'arc', height: 4, fromTree: false, toTree: false });
      // Bilder des Grafik-Leads (wenn sie da sind): die Bewegung sturzflug statt sprung, eine Spur hinter dem Vogel, der Einschlag am Ziel
      tr.acts.push({ t0: e.t, t1: e.at, anim: 'sturzflug', strong: true });
      addFx('effect', 'sturzflug-spur', e.unit, e.unit, e.t, e.at);
      // Bild 1 ist das größte; so steht es im Trefferstopp (Grafik-Lead, Version 7)
      addFx('effect', 'sturzflug-einschlag', e.target, e.unit, e.at - 0.06);
    } else if (e.type === 'damage' && e.spotOpen && e.from !== null) {
      // K51: Ein Kniff trifft die Schwachstelle; die Zielscheibe zerspringt (Bild des Grafik-Leads, wenn es da ist)
      addFx('effect', 'schwachstelle-treffer', e.to, e.from, e.t);
    } else if (e.type === 'telegraph' && e.kniff === 'baum-faellen') {
      // S13 Biber (Runde 7): Solange die Ansage läuft, nagt er am Baum; den Baum selbst zeichnet scenery.ts
      const tr = tracks.get(e.unit);
      const lead = (sprite('effect', 'baum-faellt')?.leadMs ?? 180) / 1000;
      if (tr) tr.acts.push({ t0: e.t, t1: Math.max(e.t, e.at - lead), anim: 'nagen', strong: true });
    } else if (e.type === 'telegraph' && e.kniff === 'rammstoss') {
      // K48: Der Treibholzer scharrt, solange er ausholt, und rammt im Moment des Stoßes
      const tr = tracks.get(e.unit);
      if (!tr) continue;
      addFx('effect', 'rammstaub', e.unit, e.unit, e.t, e.at);
      const a = anim(e.unit, 'rammen');
      const lead = a ? secondsUntilFrame(a, a.hitFrame ?? 1) : 0.15;
      tr.acts.push({ t0: e.at - lead, t1: e.at - lead + (a ? animSeconds(a) : 0.4), anim: 'rammen', strong: true });
    }
  }
}

/** Die Bewegung einer Figur im Moment T, nach Vorrang: Abschied, Angriff, Treffer, Laufen, Stehen */
export type Pose = {
  anim: string;
  /** Sekunden seit Beginn der Bewegung */
  at: number;
  /** T30: kurz weiß aufblitzen */
  flash: boolean;
  /** K39: Höhe über dem Boden im Sprung, in Feldeinheiten */
  lift: number;
  /** Bewegungsrichtung im Sprung oder Stoß, für Striche und Staub */
  dir: P | null;
  style: Leap['style'] | null;
  /** Abschied (Freunde) und vertrieben (Sturmwesen): 0 voll da, 1 weg */
  fade: number;
  /** T10: Sturmwesen huschen beim Vertreiben flott nach oben oder unten weg (Feldeinheiten) */
  drift: number;
  /** S5: Anteil der Höhe des Sitzplatzes im Baum, beim Absprung aus dem Baum und bei der Landung darin */
  treeLift: number;
  /** K44: schaut nach links (-1) oder rechts (1) zu seinem Ziel; 0 wie gezeichnet */
  face: -1 | 0 | 1;
  /** K44: Ausfallschritt zum Ziel beim Schlag, in Feldeinheiten */
  lunge: P | null;
  /** K44: seitlicher Weg in Feldeinheiten (bisher nur vorgesehen; wer Abschied nimmt, geht über drift nach hinten davon) */
  walkX: number;
  /** Ist er schon aus dem Kampf (gefallen, Abschied, vertrieben)? Dann zeigt das Bild noch seinen Abgang */
  down: boolean;
};

const FLASH = 0.07;
/** So blass liegt, wer gefallen ist (0 voll da, 1 weg) */
export const FALLEN_FADE = 0.55;
/** K44: Feldeinheiten pro Sekunde, mit denen ein Freund nach dem Abschied nach hinten in den Wald davongeht */
const FAREWELL_WALK = 9;
/** K44: so weit geht ein Nahkämpfer beim Schlag auf sein Ziel zu (Feldeinheiten) */
const LUNGE = 1.1;
/** Runde 8 (Grafik-Lead, Schlussrunde): Diese werfen sich schon im Bild nach vorn (Bodycheck), ohne Ausfallschritt */
const NO_LUNGE = new Set(['grosser-treibholzer']);
const STRIKE_FALLBACK = { angriff: 0.45, wurf: 0.5 };

export function poseAt(r: BattleResult, uid: number, T: number, sheet: ArtSheet | undefined): Pose {
  const m = motionOf(r);
  const tr = m.tracks.get(uid);
  const u = r.units[uid]!;
  const pose: Pose = { anim: 'stehen', at: T + uid * 0.37, flash: false, lift: 0, dir: null, style: null, fade: 0, drift: 0, treeLift: 0, face: 0, lunge: null, walkX: 0, down: false };
  if (!tr) return pose;
  const anims = sheet?.animations ?? {};
  // Treffer: kurz weiß, dann zucken (T30)
  let lastHit = -Infinity;
  for (const h of tr.hits) {
    if (h > T + 1e-6) break;
    lastHit = h;
  }
  pose.flash = T - lastHit >= 0 && T - lastHit < FLASH;
  if (tr.down !== null && T >= tr.down) {
    const age = T - tr.down;
    if (u.side === 'friend' || u.kind === 'infested') {
      // T34, F42: Abschied nimmt, wer verwundet noch einmal fällt; wer zum ersten Mal fällt (und wer befreit wird), kippt
      // nach hinten und sitzt benommen da (fallen), bleibt einen Augenblick sitzen und kommt später verwundet zurück
      const farewell = u.kind !== 'infested' && (r.friends.find((f) => f.uid === uid)?.farewell ?? true);
      const name = !farewell && anims.fallen ? 'fallen' : anims.abschied ? 'abschied' : 'treffer';
      const a = anims[name];
      const len = a ? animSeconds(a) : 1.2;
      const down = { ...pose, down: true, flash: false };
      if (!farewell) {
        // K44 (Fabi, 17.10.2026 nachts): Fallen und Abschied sollen sich unterscheiden. Wer fällt, bleibt blass liegen, bis der
        // Kampf vorbei ist (das letzte Bild von fallen); er kommt ja verwundet wieder
        return { ...down, anim: name, at: Math.min(age, len), fade: Math.min(FALLEN_FADE, Math.max(0, (age - len) / 0.6) * FALLEN_FADE) };
      }
      // Wer Abschied nimmt, sinkt zusammen (abschied) und geht dann davon, nach hinten in den Wald, nicht zum Floß:
      // Er bleibt an diesem Ufer
      if (age <= len || !anims.laufen) return { ...down, anim: name, at: age, fade: Math.min(1, Math.max(0, (age - len) / 0.45)) };
      const away = age - len;
      // Version 4 des Manifests: weggehen, müde, mit einem Blick zurück, schon in Gehrichtung gezeichnet
      const w = anims.weggehen;
      if (w) {
        const total = w.sequence.reduce((a, [, ms]) => a + ms, 0) / 1000;
        const fadeFrom = total - (w.fadeMs ?? 600) / 1000;
        return { ...down, anim: 'weggehen', at: away, walkX: walkedAway(w, away), fade: Math.min(1, Math.max(0, (away - fadeFrom) / Math.max(0.05, total - fadeFrom))) };
      }
      return { ...down, anim: 'laufen', at: away, drift: -away * FAREWELL_WALK, fade: Math.min(1, Math.max(0, (away - 1.2) / 1.4)) };
    }
    // T10: vertrieben, verpuffen und flott weg
    return { ...pose, down: true, anim: anims.vertrieben ? 'vertrieben' : 'treffer', at: age, flash: false, fade: Math.min(1, age / 0.38), drift: (uid % 2 === 0 ? -1 : 1) * age * 26 };
  }
  // Sprung, Huschen, Stoß (K39)
  let leap: Leap | null = null;
  for (const l of tr.leaps) {
    if (T < l.t0 || T > l.t1) continue;
    const k = (T - l.t0) / Math.max(1e-6, l.t1 - l.t0);
    pose.lift = l.style === 'arc' ? l.height * 4 * k * (1 - k) : 0;
    pose.dir = { x: l.to.x - l.from.x, y: l.to.y - l.from.y };
    pose.style = l.style;
    pose.treeLift = (l.fromTree ? 1 - k : 0) + (l.toTree ? k : 0);
    leap = l;
  }
  pose.face = faceAt(r, uid, T);
  // Erdüberraschung: Das Ziel wird kurz hochgehoben, wenn der Boden aufbricht
  for (const b of tr.bumps) if (T >= b && T - b < 0.3) pose.lift += 2.5 * Math.sin((Math.PI * (T - b)) / 0.3);
  const actAt = (strong: boolean): Pose | null => {
    for (let i = tr.acts.length - 1; i >= 0; i--) {
      const a = tr.acts[i]!;
      if (a.strong === strong && T >= a.t0 && T <= a.t1 && anims[a.anim]) return { ...pose, anim: a.anim, at: T - a.t0 };
    }
    return null;
  };
  // Version 3: Eingraben, Auftauchen und Rollen gehen allem vor
  const strong = actAt(true);
  if (strong) return strong;
  // F35 Einrollen und Stachelkugel: eine Kugel (das erste Bild von rollen)
  if (anims.rollen && flagsAt(r, uid, T) & FrameFlag.rolled) return { ...pose, anim: 'rollen', at: 0 };
  // Angriff oder Wurf, so gelegt, dass das Trefferbild oder die Landung auf den Treffer fällt
  for (let i = tr.strikes.length - 1; i >= 0; i--) {
    const s = tr.strikes[i]!;
    const name = s.anim && anims[s.anim] ? s.anim : s.ranged ? (anims.wurf ? 'wurf' : 'angriff') : anims.angriff ? 'angriff' : 'wurf';
    const a = anims[name];
    const lead = a ? secondsUntilFrame(a, (s.ranged ? a.releaseFrame : a.hitFrame) ?? a.hitFrame ?? a.releaseFrame ?? 1) : 0.15;
    const start = (s.ranged ? s.release : s.t) - lead;
    const len = a ? animSeconds(a) : STRIKE_FALLBACK[name as 'angriff' | 'wurf'];
    if (start > T) continue;
    if (T <= start + len) {
      // K44: Wer schlägt oder wirft, schaut zu seinem Ziel; Nahkämpfer machen dabei einen kleinen Schritt auf das Ziel zu,
      // auch nach oben oder unten. So sieht man, wohin der Schlag geht, ohne Bilder für acht Richtungen
      const me = posAt(r, uid, s.t);
      const to = posAt(r, s.to, s.t);
      const dx = to.x - me.x;
      const dy = to.y - me.y;
      const d = Math.hypot(dx, dy);
      const out: Pose = { ...pose, anim: name, at: T - start, face: Math.abs(dx) > 0.6 ? (dx < 0 ? -1 : 1) : faceAt(r, uid, T) };
      if (!s.ranged && d > 1e-6 && s.to !== uid && !NO_LUNGE.has(u.defId)) {
        const k = Math.max(0, Math.min(1, (T - (s.t - 0.14)) / 0.32));
        const amp = Math.sin(Math.PI * k) * LUNGE;
        if (amp > 0.01) out.lunge = { x: (dx / d) * amp, y: (dy / d) * amp };
      }
      return out;
    }
    break;
  }
  const weak = actAt(false);
  if (weak) return weak;
  // Version 3: im Sprung die Sprungbewegung, beim Huschen das Huschen (Schleifen; den Bogen macht lift)
  if (leap && leap.style === 'arc' && anims.sprung) return { ...pose, anim: 'sprung', at: T - leap.t0 };
  if (leap && leap.style === 'dash' && anims.huschen) return { ...pose, anim: 'huschen', at: T - leap.t0 };
  const treffer = anims.treffer;
  if (treffer && T - lastHit >= 0 && T - lastHit < animSeconds(treffer)) return { ...pose, anim: 'treffer', at: T - lastHit, flash: false };
  if (pose.style === null && tr.walk(T)) return { ...pose, anim: 'laufen', at: T };
  return pose;
}

const posAt = (r: BattleResult, uid: number, T: number): P => {
  const f = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.round(T / combatRules.tickSeconds)))];
  const d = f?.units[uid];
  return d ? { x: d[0], y: d[1] } : { x: 0, y: 0 };
};

/**
 * K44: Wer ein Ziel hat, schaut dorthin, auch beim Laufen und zwischen zwei Schlägen; steht das Ziel fast genau darüber
 * oder darunter, bleibt die gezeichnete Richtung (sonst flackert die Figur)
 */
export function faceAt(r: BattleResult, uid: number, T: number): -1 | 0 | 1 {
  const to = targetAt(r, uid, T);
  if (to < 0 || to === uid) return 0;
  const dx = posAt(r, to, T).x - posAt(r, uid, T).x;
  return Math.abs(dx) > 1.5 ? (dx < 0 ? -1 : 1) : 0;
}

/**
 * Version 4, weggehen: wie weit die Figur nach `at` Sekunden gegangen ist, in Feldeinheiten, mit Vorzeichen der
 * Gehrichtung. In den Schritten aus stillSteps steht sie (müde dastehen, umdrehen, zurückschauen)
 */
export function walkedAway(a: ArtAnim, at: number): number {
  const still = new Set(a.stillSteps ?? []);
  const speed = a.speed ?? 6;
  let t = at;
  let walked = 0;
  for (let i = 0; i < a.sequence.length && t > 0; i++) {
    const step = Math.min(t, a.sequence[i]![1] / 1000);
    if (!still.has(i)) walked += step * speed;
    t -= step;
  }
  return (a.facing === 'right' ? 1 : -1) * walked;
}

/** Zustände einer Figur im Bild zur Zeit T (FrameFlag) */
export function flagsAt(r: BattleResult, uid: number, T: number): number {
  const tick = combatRules.tickSeconds;
  const f = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.round(T / tick)))];
  return f?.units[uid]?.[4] ?? 0;
}

/** Wen die Figur im Bild zur Zeit T angreift (uid oder -1) */
export function targetAt(r: BattleResult, uid: number, T: number): number {
  const tick = combatRules.tickSeconds;
  const f = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.round(T / tick)))];
  return f?.units[uid]?.[6] ?? -1;
}

/** Läuft, springt oder huscht die Figur gerade? (K38 Hastig) */
export function movingAt(r: BattleResult, uid: number, T: number): boolean {
  const tr = motionOf(r).tracks.get(uid);
  if (!tr) return false;
  return tr.walk(T) || tr.leaps.some((l) => T >= l.t0 && T <= l.t1);
}

/** K39: Wo die Figur im Sprung gerade ist (statt des Platzes aus der Simulation) */
export function leapPosition(r: BattleResult, uid: number, T: number): P | null {
  const tr = motionOf(r).tracks.get(uid);
  if (!tr) return null;
  for (const l of tr.leaps) {
    if (T < l.t0 || T > l.t1) continue;
    const k = (T - l.t0) / Math.max(1e-6, l.t1 - l.t0);
    const e = l.style === 'slide' ? 1 - (1 - k) * (1 - k) : k < 0.5 ? 2 * k * k : 1 - 2 * (1 - k) * (1 - k);
    return { x: l.from.x + (l.to.x - l.from.x) * e, y: l.from.y + (l.to.y - l.from.y) * e };
  }
  return null;
}
